/**
 * 管理模式顶部提示条
 * 在管理模式下显示在页面顶部，显示剩余时间和退出按钮
 */

import React, { useMemo } from 'react';
import { Button, Space } from 'antd';
import {
  ToolOutlined,
  ClockCircleOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { useAdminMode } from '../../contexts/AdminModeContext';
import './index.less';

interface AdminModeBannerProps {
  /** 退出后的回调 */
  onExit?: () => void;
  /** 紧凑模式（移动端） */
  compact?: boolean;
}

/**
 * 格式化剩余时间
 */
const formatRemainingTime = (seconds: number): string => {
  if (seconds <= 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/**
 * 获取时间状态颜色
 */
const getTimeStatusClass = (seconds: number): string => {
  if (seconds <= 60) return 'countdown-danger'; // 危险 - 1分钟内
  if (seconds <= 300) return 'countdown-warning'; // 警告 - 5分钟内
  return 'countdown-normal'; // 正常
};

const AdminModeBanner: React.FC<AdminModeBannerProps> = ({ onExit, compact = false }) => {
  const {
    isInAdminMode,
    isLoading,
    remainingSeconds,
    exitAdminMode,
  } = useAdminMode();

  // 处理退出
  const handleExit = async () => {
    const success = await exitAdminMode('manual');
    if (success && onExit) {
      onExit();
    }
  };

  // 格式化时间
  const formattedTime = useMemo(() => formatRemainingTime(remainingSeconds), [remainingSeconds]);
  const timeStatusClass = useMemo(() => getTimeStatusClass(remainingSeconds), [remainingSeconds]);

  // 不在管理模式时不显示
  if (!isInAdminMode) {
    return null;
  }

  // 紧凑模式（移动端）- 简化显示
  if (compact) {
    return (
      <div className="admin-mode-banner admin-mode-banner-compact">
        <div className="banner-content">
          <Space className="banner-text">
            <ToolOutlined />
            <span className={`banner-countdown-inline ${timeStatusClass}`}>{formattedTime}</span>
          </Space>
          <Button
            size="small"
            type="text"
            icon={<LogoutOutlined />}
            onClick={handleExit}
            loading={isLoading}
            className="banner-exit-btn"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-mode-banner">
      <div className="banner-content">
        <Space className="banner-text">
          <ToolOutlined />
          <span>管理模式</span>
        </Space>
        <Space className={`banner-countdown ${timeStatusClass}`}>
          <ClockCircleOutlined />
          <span>{formattedTime}</span>
        </Space>
        <Button
          size="small"
          type="text"
          icon={<LogoutOutlined />}
          onClick={handleExit}
          loading={isLoading}
          className="banner-exit-btn"
        >
          退出
        </Button>
      </div>
    </div>
  );
};

export default AdminModeBanner;
